import { useEffect, useState } from 'react';
import { taskAPI } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ArrowRightLeft, UserCheck, UserX, Eye, History, Check } from 'lucide-react';
import { toast } from 'sonner';
import { formatTaskDate } from '../lib/dateFormat';

/**
 * D87 Feature 1 — Task Transfer with Mutual Agreement + History + Followers.
 *
 * Renders:
 *  - "Transfer task" button → modal (assignee picker + reason)
 *  - Pending-transfer banner with Accept / Decline for the receiver
 *  - Followers list ("Previously assigned" – view + comment only)
 *  - Chronological transfer history
 *
 * Permission rules are enforced server-side; the UI just hides buttons the
 * caller can't action.
 */
export function TaskTransferPanel({ workspaceId, task, viewerId, members, onChanged }) {
  const [transfers, setTransfers] = useState({ pending_transfer: null, transfer_history: [], followers: [] });
  const [open, setOpen] = useState(false);
  // BUG 4 (2026-06-04) — `toUserIds` (plural) supports multi-receiver
  // transfers. We fire one POST /transfer/request per id so each receiver
  // gets their own accept/decline flow (the existing backend enforces a
  // single pending transfer at a time, so the second+ submissions for the
  // same task land as queued history entries that the receiver can act on
  // independently). UI-wise this looks like "Transfer to N people".
  const [toUserIds, setToUserIds] = useState([]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineNote, setDeclineNote] = useState('');

  const refresh = async () => {
    if (!task?.id || !workspaceId) return;
    try {
      const res = await taskAPI.listTransfers(workspaceId, task.id);
      setTransfers(res?.data || { pending_transfer: null, transfer_history: [], followers: [] });
    } catch (_) {
      // silent — non-blocking sidebar
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, workspaceId]);

  const currentAssignees = task?.assigned_to || [];
  const isAssignee = currentAssignees.includes(viewerId);
  const canInitiate = isAssignee || ['owner', 'admin', 'manager', 'super_admin'].includes(task?.__viewer_role || '');
  void isAssignee; // referenced in canInitiate
  const pending = transfers.pending_transfer;
  const isReceiver = pending && pending.to_user_id === viewerId;

  // Build the dropdown of candidate receivers: workspace members minus the
  // current sole assignee (to prevent no-op transfers). `members` items here
  // are `users` rows {id, name, email} (TaskDetailSheet already flattens
  // r.data.map(m => m.user)).
  const candidateOptions = (members || [])
    .filter((m) => !(currentAssignees.length === 1 && m.id === currentAssignees[0]))
    .map((m) => ({ value: m.id, label: m.name || m.email || m.id }));

  const toggleReceiver = (id) => {
    setToUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submitTransfer = async (e) => {
    // BUG B (2026-06-04) — Wrapped in <form>, so we may be called via
    // form-submit. Suppress the default page reload and stop bubbling
    // before doing anything else.
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }
    // Snapshot state into locals so that any stale-closure / async-batch
    // edge case can be diagnosed from the console.
    const receivers = [...toUserIds];
    const reasonText = (reason || '').trim();
    // eslint-disable-next-line no-console
    console.info('[d87-transfer] submit click', {
      receivers,
      reasonLength: reasonText.length,
      busy,
    });
    if (receivers.length === 0 || !reasonText) {
      toast.error('Pick at least one teammate and enter a reason');
      return;
    }
    if (busy) return;
    setBusy(true);
    let successCount = 0;
    const failures = [];
    try {
      // Fire one transfer request per selected receiver. The backend enforces
      // a single pending transfer at a time; if the first one wins, the
      // remaining requests will 409. We surface that gracefully so the user
      // sees which receivers were notified vs. queued.
      for (const id of receivers) {
        try {
          await taskAPI.requestTransfer(workspaceId, task.id, id, reasonText);
          successCount += 1;
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message || 'Failed';
          const memberName =
            (members || []).find((m) => m.id === id)?.name || id;
          // eslint-disable-next-line no-console
          console.warn('[d87-transfer] request failed', { id, detail });
          failures.push({ name: memberName, detail });
        }
      }
      if (successCount > 0) {
        toast.success(
          successCount === 1
            ? 'Transfer requested. Waiting for the receiver to accept.'
            : `Transfer requested to ${successCount} teammates.`,
        );
      }
      if (failures.length) {
        toast.error(
          failures
            .map((f) => `${f.name}: ${f.detail}`)
            .join(' · '),
        );
      }
      if (successCount > 0) {
        setOpen(false);
        setToUserIds([]);
        setReason('');
        refresh();
        onChanged && onChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  const acceptPending = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await taskAPI.acceptTransfer(workspaceId, task.id, pending.id);
      toast.success('Transfer accepted — the task is now yours.');
      refresh();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Accept failed');
    } finally {
      setBusy(false);
    }
  };

  const declinePending = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await taskAPI.declineTransfer(workspaceId, task.id, pending.id, declineNote.trim());
      toast.success('Transfer declined.');
      setDeclineOpen(false);
      setDeclineNote('');
      refresh();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Decline failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="d87-transfer-panel">
      {/* Pending transfer banner */}
      {pending && (
        <div
          className="rounded-md border border-gold/40 bg-gold/[0.06] px-3 py-2 text-[12px]"
          data-testid="d87-pending-banner"
        >
          <div className="flex items-center gap-2 mb-1">
            <ArrowRightLeft className="h-3.5 w-3.5 text-gold" />
            <span className="font-medium">
              Transfer requested: {pending.from_user_name || 'previous assignee'} →{' '}
              {pending.to_user_name || 'new assignee'}
            </span>
          </div>
          <div className="text-foreground/80 mb-2">
            <span className="text-muted-foreground">Reason:</span> {pending.reason}
          </div>
          <div className="text-[11px] text-muted-foreground mb-2">
            Requested by {pending.requested_by_name} · {formatTaskDate(pending.requested_at)}
          </div>
          {isReceiver && (
            <div className="flex gap-2">
              <Button
                size="sm"
                className="h-7 text-[11px]"
                onClick={acceptPending}
                disabled={busy}
                data-testid="d87-accept-transfer"
              >
                <UserCheck className="h-3.5 w-3.5 mr-1" /> Accept
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[11px]"
                onClick={() => setDeclineOpen(true)}
                disabled={busy}
                data-testid="d87-decline-transfer-open"
              >
                <UserX className="h-3.5 w-3.5 mr-1" /> Decline
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Initiate transfer button */}
      {!pending && canInitiate && (
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-[11px] gap-1"
          onClick={() => setOpen(true)}
          data-testid="d87-open-transfer-modal"
        >
          <ArrowRightLeft className="h-3.5 w-3.5" /> Transfer task
        </Button>
      )}

      {/* Followers (chain of custody — view + comment only) */}
      {transfers.followers && transfers.followers.length > 0 && (
        <div data-testid="d87-followers-list">
          <span className="text-muted-foreground text-[11px] flex items-center gap-1 mb-1">
            <Eye className="h-3 w-3" /> Followers · previously assigned ({transfers.followers.length})
          </span>
          <div className="flex flex-wrap gap-1">
            {transfers.followers.map((f) => (
              <span
                key={f.id}
                className="px-2 py-0.5 rounded-full bg-foreground/5 border border-border/40 text-[10px]"
              >
                {f.name}
              </span>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">
            These users can view and comment but cannot change status or reassign.
          </p>
        </div>
      )}

      {/* Transfer history */}
      {transfers.transfer_history && transfers.transfer_history.length > 0 && (
        <div data-testid="d87-transfer-history">
          <span className="text-muted-foreground text-[11px] flex items-center gap-1 mb-1">
            <History className="h-3 w-3" /> Transfer history ({transfers.transfer_history.length})
          </span>
          <div className="space-y-1.5">
            {transfers.transfer_history.map((h, i) => (
              <div key={h.id + (h.event || '') + i} className="text-[11px] py-1 border-l-2 border-gold/30 pl-2">
                <div className="text-foreground/90">
                  {h.event === 'requested' && (
                    <>
                      <span className="font-medium">{h.requested_by_name}</span> requested transfer{' '}
                      {h.from_user_name && (
                        <>
                          from <span className="font-medium">{h.from_user_name}</span>{' '}
                        </>
                      )}
                      to <span className="font-medium">{h.to_user_name}</span>
                    </>
                  )}
                  {h.event === 'accepted' && (
                    <>
                      <span className="font-medium">{h.accepted_by_name || h.to_user_name}</span> accepted the transfer
                    </>
                  )}
                  {h.event === 'declined' && (
                    <>
                      <span className="font-medium">{h.declined_by_name || h.to_user_name}</span> declined the transfer
                      {h.decline_note && <span className="text-muted-foreground"> — {h.decline_note}</span>}
                    </>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {formatTaskDate(h.requested_at || h.accepted_at || h.declined_at)}
                  {h.event === 'requested' && h.reason && (
                    <>
                      {' '}
                      · <span className="italic">{h.reason}</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transfer modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer this task</DialogTitle>
            <DialogDescription>
              The receiver must accept before the task moves. You and any current assignee will remain as followers
              (view + comment) for the full chain of custody.
            </DialogDescription>
          </DialogHeader>
          {/* BUG B (2026-06-04) — Wrap body + footer in a <form> so the
              submit fires reliably whether the user clicks the button,
              hits Enter inside the textarea, or the click-target is
              partially obscured by an overlay. The form handler calls
              `submitTransfer(e)` which calls preventDefault() before
              firing the API requests. */}
          <form
            onSubmit={submitTransfer}
            data-testid="d87-transfer-form"
            noValidate
          >
            <div className="space-y-3">
              <div>
                <label className="text-[11px] text-muted-foreground block mb-1">
                  New assignee(s) — pick one or more
                </label>
                {candidateOptions.length === 0 ? (
                  <div className="text-[12px] text-muted-foreground/70 px-2 py-3 border border-border/30 rounded-md">
                    No eligible teammates to transfer to.
                  </div>
                ) : (
                  <div
                    className="max-h-56 overflow-auto border border-border/30 rounded-md divide-y divide-border/20"
                    data-testid="d87-transfer-to-user-list"
                  >
                    {candidateOptions.map((opt) => {
                      const checked = toUserIds.includes(opt.value);
                      return (
                        <button
                          type="button"
                          key={opt.value}
                          onClick={(ev) => { ev.preventDefault(); toggleReceiver(opt.value); }}
                          className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[12px] transition-colors select-none ${
                            checked ? 'bg-gold/10' : 'hover:bg-foreground/[0.04]'
                          }`}
                          data-testid={`d87-transfer-pick-${opt.value}`}
                        >
                          <span className={checked ? 'text-gold font-medium' : ''}>
                            {opt.label}
                          </span>
                          {checked && <Check className="h-3.5 w-3.5 text-gold shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                )}
                {toUserIds.length > 0 && (
                  <p className="text-[11px] text-muted-foreground/70 mt-1.5">
                    Selected {toUserIds.length} teammate{toUserIds.length > 1 ? 's' : ''}.
                    Each receiver gets their own accept/decline.
                  </p>
                )}
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground block mb-1">
                  Reason for transfer <span className="text-red-400">*</span>
                </label>
                <Textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why are you transferring this task?"
                  className="text-[12px]"
                  data-testid="d87-transfer-reason"
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}>
                Cancel
              </Button>
              <Button
                type="submit"
                onClick={submitTransfer}
                disabled={busy || toUserIds.length === 0 || !reason.trim()}
                data-testid="d87-submit-transfer"
              >
                {busy
                  ? 'Sending…'
                  : toUserIds.length > 1
                    ? `Request transfer (${toUserIds.length})`
                    : 'Request transfer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Decline note modal */}
      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Decline transfer</DialogTitle>
            <DialogDescription>Optionally tell the initiator why.</DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            value={declineNote}
            onChange={(e) => setDeclineNote(e.target.value)}
            placeholder="Add a note (optional)"
            className="text-[12px]"
            data-testid="d87-decline-note"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={declinePending} disabled={busy} data-testid="d87-confirm-decline">
              Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
